import context from '@truckdown/components-systems';
import { SetRecurringChargeModel, PromotionModel, IPromotionManager, PromotionBillingPeriod } from '@truckdown/promotions';
import { defineComponent, ref, Ref, onMounted, computed } from "vue";
import validationError from '../../Validation/Error/Error.vue';
import locationLookup from '../LocationLookup/LocationLookup.vue';
import { showMessage, isCsrOrAdmin } from '../../Utilities/utilities'
import { CreditCardModel, CreditCardQueryOptions, ICreditCardService, CreditCardStatus } from "@truckdown/billing";
import addCreditCard from "../AddCreditCard/AddCreditCard.vue";

interface IData {
    isLoading: Ref<boolean>,
    errors: Ref<{ [key: string]: string }>,
    disableRecurringCharge: Ref<boolean>,

    balance: Ref<number>,
    frequency: Ref<PromotionBillingPeriod>,
    selectedCreditCard: Ref<CreditCardModel | null>,
    securityCode: Ref<string>,

    displayAddCreditCard: Ref<boolean>
};

const getInitialData = function (props: any) {
    return {
        isLoading: ref<boolean>(false),
        errors: ref<{ [key: string]: string }>({}),
        disableRecurringCharge: ref<boolean>(false),

        balance: ref<number>(props.promotion.funds.recurringCharge?.toBalance ?? 0),
        frequency: ref<PromotionBillingPeriod>(props.promotion.funds.recurringCharge?.period ?? PromotionBillingPeriod.Monthly),
        selectedCreditCard: ref<CreditCardModel | null>(null),
        securityCode: ref<string>(''),

        displayAddCreditCard: ref<boolean>(false)
    } as IData;
}

const getComputed = function (data: IData) {
    return {
        displayError: computed(() => {
            let errors = data.errors.value;
            let keys = Object.keys(errors);

            let response = {} as { [key: string]: boolean };
            for (let i = 0; i < keys.length; i++) {
                if (errors[keys[i]]) {
                    response[keys[i]] = true;
                }
            }
            return response;
        })
    }
}

const getMethods = function (props: any, data: IData, ctx: any) {
    return {
        validate: function () {
            validateRecurringCharge(data);
        },
        recurringChargeCancelClick: function (): void {
            ctx.emit(events.cancel);
        },
        recurringChargeSaveClick: async function (): Promise<void> {
            await saveRecurringCharge(data, props, ctx);
        },
        creditCardChange: function (ev: Event) {
            if (data.selectedCreditCard.value === null) {
                data.displayAddCreditCard.value = true;
            } else {
                data.displayAddCreditCard.value = false;
            }
        },
        addCreditCardCancel: async function () {
            data.displayAddCreditCard.value = false;
        },
        addCreditCardSave: async function (card: CreditCardModel) {
            if (card) {
                data.selectedCreditCard.value = card;
                data.displayAddCreditCard.value = false;
            }
        }

    }
}

const hasErrors = function (data: IData) {
    return Object.keys(data.errors.value).length > 0;
}

const validateRecurringCharge = function (data: IData) {
    data.errors.value = {};

    if (!data.disableRecurringCharge.value) {
        if (data.balance.value <= 0) {
            data.errors.value['balance'] = 'Please enter the balance to increase to for each charge.';
        } else if (data.balance.value > 0) {
            if (!data.frequency.value) {
                data.errors.value['frequency'] = 'Please select a frequency for your recurring charge.';
            }
            if (!data.selectedCreditCard.value) {
                data.errors.value['selectedCreditCard'] = 'Please select or add a credit card.';
            }
            if (!isCsrOrAdmin() && (!data.securityCode.value || data.securityCode.value.length < 2)) {
                data.errors.value['securityCode'] = 'Please enter a valid security code for your credit card.';
            }
        }
    }
}

const saveRecurringCharge = async function (data: IData, props: any, ctx: any): Promise<void> {
    if (data.disableRecurringCharge.value) {
        await tryDisableRecurringCharge(data, props, ctx);
    } else {
        await trySetRecurringCharge(data, props, ctx);
    }
}

const trySetRecurringCharge = async function (data: IData, props: any, ctx: any): Promise<void> {
    validateRecurringCharge(data);

    if (!hasErrors(data)) {
        if (!data.isLoading.value) {
            data.isLoading.value = true;
            try {
                let model: SetRecurringChargeModel = {
                    creditCardId: data.selectedCreditCard.value?.id ?? '',
                    securityCode: data.securityCode.value,
                    toBalance: data.balance.value,
                    period: data.frequency.value,
                    promotionId: props.promotion.id
                };

                let mgr = await context.getService<IPromotionManager>('IPromotionManager');
                var response = await mgr.setRecurringCharge(model);
                if (!response || !response.ok) {
                    showMessage("Error saving recurring billing options.", "danger", true, true);
                    ctx.emit(events.cancel);
                } else {
                    showMessage("Recurring Billing Options saved successfully.", "success", true, true);
                    ctx.emit(events.update, response.data);
                }
            }
            catch (err) {
                console.log('Error saving recurring charge.', err);
                showMessage("Error saving recurring charge", "danger", true, true);
                ctx.emit(events.cancel);
            }
            finally {
                data.isLoading.value = false;
            }
        }
    }
}

const tryDisableRecurringCharge = async function (data: IData, props: any, ctx: any): Promise<void> {
    if (!data.isLoading.value) {
        data.isLoading.value = true;
        try {
            let mgr = await context.getService<IPromotionManager>('IPromotionManager');
            var response = await mgr.removeRecurringCharge(props.promotion.id);
            if (!response || !response.ok) {
                showMessage("Error saving recurring billing options.", "danger", true, true);
                ctx.emit(events.cancel);
            } else {
                showMessage("Recurring Billing Options saved successfully.", "success", true, true);
                ctx.emit(events.update, response.data);
            }
        }
        catch (err) {
            console.log('Error saving recurring charge.', err);
            showMessage("Error saving recurring charge", "danger", true, true);
            ctx.emit(events.cancel);
        }
        finally {
            data.isLoading.value = false;
        }
    }
}

const loadCreditCard = async function (data: IData, props: any, ctx: any) {

    try {
        let creditCardService = await context.getService<ICreditCardService>('ICreditCardService');
        var card = await creditCardService.getPrimaryCard(props.promotion.accountId);

        data.selectedCreditCard.value = card;

    } catch (err) {
        console.log('Error loading credit cards.', err);
        showMessage("Error loading credit cards", "danger", true, true);
        ctx.emit(events.cancel);
    }
}

const events = {
    update: 'update',
    cancel: 'cancel'
}

export default defineComponent({
    props: {
        promotion: {
            type: Object as () => PromotionModel,
            required: true
        },
        taxname: {
            type: String,
            required: true
        },
        taxrate: {
            type: Number,
            required: true
        }
    },
    components: {
        'error': validationError,
        'location-lookup': locationLookup,
        'add-credit-card': addCreditCard
    },
    emits: [events.cancel, events.update],
    setup: function (props, ctx) {
        let data = getInitialData(props);
        let computed = getComputed(data);
        let methods = getMethods(props, data, ctx);

        onMounted(async () => {
            await loadCreditCard(data, props, ctx);
        });

        return {
            ...data,
            ...computed,
            ...methods,
            'PromotionBillingPeriod': PromotionBillingPeriod,
            'isCsrOrAdmin': isCsrOrAdmin
        };
    }
});